









































import { Prop, Component, Emit, Vue, Watch } from 'vue-property-decorator';
import { IField, IMergeValues } from '@lib-shared/editor/types';
import { EContext } from '@lib-shared/editor/types/enums';
import ProsemirrorViewerFormInput from '@/editor/editor-block-view/components/ProsemirrorViewerFormInput.vue';
import { ProsemirrorHelper } from '@lib-shared/editor/helpers/prosemirror.helper';
import { IUser } from '@lib-shared/api/users/types/user';
import { IRecipient } from '@lib-shared/types/Recipient';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';
import { EditorFormFieldColor } from '@lib-shared/types/editor-form-field-color';
import { SigningHelper } from '@lib-shared/helpers/signing.helper';
import { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { ETooltipPlacement } from '@lib-shared/enums/tooltip-placement';
import { ITooltip } from '@lib-shared/types/tooltip';

@Component({
  components: {
    ProsemirrorViewerFormInput,
  },
  data: () => ({
    ProsemirrorHelper,
  }),
})
export default class InputText extends Vue {
  @Prop({ default: () => EContext.Document }) context: EContext;
  @Prop() fields: IField[];
  @Prop() focusedRequiredId?: string;
  @Prop() input: IField;
  @Prop() mergeValues: IMergeValues;
  @Prop() isSigningSite: boolean;
  @Prop() sender: IUser;
  @Prop() recipient: IRecipient;
  @Prop() recipientInput!: IEditorRecipientInput[];

  @Emit('update-value') updateValue(payload: string) {
    return payload;
  }

  focus = false;
  @Watch('focusedRequiredId')
  watchFocusedRequiredId(focusedRequiredId: string) {
    if (!this.isAssignedToCurrentUser) {
      return;
    }
    this.focus = this.input.id === focusedRequiredId;
  }

  get isEditable() {
    return (
      this.isAssignedToCurrentUser &&
      !this.hasRecipientSigned &&
      this.context !== EContext.DocumentView
    );
  }

  get isSigningSiteAndAssignedToRecipient() {
    return this.isSigningSite && this.recipient?.id === this.input?.recipientId;
  }

  get isAppSiteAndAssignedToSender() {
    return !this.isSigningSite && this.sender?.id === this.input?.userId;
  }

  get isAssignedToCurrentUser() {
    return this.isSigningSiteAndAssignedToRecipient || this.isAppSiteAndAssignedToSender;
  }

  get hasRecipientSigned(): boolean {
    return SigningHelper.hasRecipientSigned(this.recipient?.status as ERecipientStatus);
  }

  get color(): EditorFormFieldColor {
    if (this.recipient?.id === this.input?.recipientId) {
      return this.isSigningSite ? 'white' : 'green';
    }

    if (this.isAppSiteAndAssignedToSender && this.context !== EContext.DocumentView) {
      return 'orange';
    }

    return 'grey';
  }

  get inputValue(): string {
    if (this.input.userId) {
      return this.input.value || '';
    }

    const fieldValue = this.recipientInput?.find(field => field.fieldId === this.input.id)?.value;
    if (fieldValue) {
      return fieldValue;
    }

    return this.input.inputSettings.defaultValue || '';
  }

  get assignedRecipient(): IRecipient {
    return this.mergeValues?.recipients?.find(recipient => recipient.id === this.input.recipientId);
  }

  get tooltip(): ITooltip {
    return {
      content: this.isAssignedToCurrentUser
        ? this.$t('field-assigned-to-you').toString()
        : `${this.$t('field-assigned-to').toString()} ${this.assigneeFullName}`,
      offset: 10,
      placement: ETooltipPlacement.Top,
    };
  }

  get assigneeFullName(): string {
    const fullName =
      this.sender?.id === this.input?.userId
        ? this.sender?.fullName
        : this.assignedRecipient?.fullName;
    return fullName || '';
  }

  getSafeText(text: null | string): string {
    return text ? JSON.parse(text) : '';
  }

  handleInputUpdate(value: string) {
    if (this.inputValue === value) {
      return;
    }
    this.updateValue(value);
  }
}
